import axios from 'axios'

import type { GridData, FilterValue, SortConfig, FilterOption, FeedbackRow } from '../types/feedbackGrid.types'

export class FeedbackGridApi {
	private endpoints: Record<string, string>

	constructor(endpoints: Record<string, string>) {
		this.endpoints = endpoints
	}

	async fetchData(offset: number, limit: number, filters: FilterValue, sort: SortConfig | null): Promise<GridData> {
		const response = await axios.post(this.endpoints.fetchData, {
			offset,
			limit,
			filters,
			sort,
		})

		return response.data
	}

	async applyFilters(filters: FilterValue): Promise<void> {
		const response = await axios.post(this.endpoints.applyFilters, {
			filters,
		})

		if (!response.data.success) {
			throw new Error(response.data.error || 'Failed to apply filters')
		}
	}

	async fetchFilterOptions(filterKey: string): Promise<FilterOption[]> {
		const response = await axios.post(this.endpoints.fetchFilterOptions, {
			filterKey,
		})

		if (!response.data.success) {
			throw new Error(response.data.error || 'Failed to fetch filter options')
		}

		return response.data.options
	}

	async fetchRowActions(feedbackId: number): Promise<FeedbackRow> {
		const response = await axios.post(this.endpoints.fetchRowActions, {
			feedbackId,
		})

		if (!response.data.success) {
			throw new Error(response.data.error || 'Failed to fetch row actions')
		}

		return response.data.data
	}

	async updateCell(
		feedbackId: number,
		field: string,
		value: any,
	): Promise<{ success: boolean; data?: any; error?: string }> {
		const response = await axios.post(this.endpoints.updateCell, {
			feedbackId,
			field,
			value,
		})

		return response.data
	}

	exportData(): void {
		window.location.href = this.endpoints.export
	}

	async testBulkNotification(feedbackIds: number[]): Promise<{
		success: boolean
		settings: Array<{
			settingId: number
			settingName: string
			groups: Array<{
				emails: string[]
				feedbacks: Array<{ id: number }>
				subject: string
				html: string
			}>
		}>
		error?: string
	}> {
		const response = await axios.post(this.endpoints.testBulkNotification, { feedbackIds })
		return response.data
	}

	async sendBulkNotification(feedbackIds: number[]): Promise<{ success: boolean; error?: string }> {
		const response = await axios.post(this.endpoints.sendBulkNotification, { feedbackIds })
		return response.data
	}

	async getCategoryEditData(feedbackId: number): Promise<{
		success: boolean
		data?: {
			questions: number[]
			currentCategories: Record<string, Record<number, number | null>>
			editAllowed: boolean
			isUserCategoryManager: boolean
			treeSelectEndpoint: string
		}
		error?: string
	}> {
		const response = await axios.post(this.endpoints.getCategoryEditData, {
			feedbackId,
		})

		return response.data
	}

	async saveCategoryEdit(
		feedbackId: number,
		categories: Record<string, Record<number, number | null>>,
	): Promise<{
		success: boolean
		error?: string
		data?: {
			annotated: boolean
			checked: boolean
			sentiment: number | null
			sentiment_color: string | null
			sentiment_label: string | null
		}
	}> {
		const response = await axios.post(this.endpoints.saveCategoryEdit, {
			feedbackId,
			categories,
		})

		return response.data
	}
}
